import React, { Component } from "react";
import { Container } from "reactstrap";
import {
  closeChat,
  sendMessageAdmin,
  getChatListAdmin,
  getChatListAdminById,
} from "../../Endpoint/Admin/index";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { ToastContainer, toast } from "react-toastify";
import "./chat.css";
import moment from "moment-timezone";

class ChatList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      isLoaded: false,
      selectedChat: null,
      selectedChatHistory: [],
      chatMasterId: "",
      message: "",
      searchQuery: "",
      isSending: false,
      breadcrumbItems: [
        { title: "Chat", link: "#" },
        { title: "Messages", link: "#" },
      ],
    };
    this.messagesEndRef = React.createRef();
  }

  componentDidMount() {
    this.getAllChats();
    // Start auto-refresh polling every 15 seconds
    this.pollInterval = setInterval(() => {
      this.pollForUpdates();
    }, 15000);
  }

  componentWillUnmount() {
    // Clean up polling interval
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.selectedChatHistory !== this.state.selectedChatHistory) {
      this.scrollToBottom();
    }
  }

  pollForUpdates = () => {
    // Skip polling if user is typing a message (to avoid disruption)
    if (this.state.message && this.state.message.trim().length > 0) {
      return;
    }

    // Refresh chat list silently (without loading state)
    getChatListAdmin().then((result) => {
      if (result && result.chatData) {
        this.setState({ items: result.chatData });
      }
    });

    // If a chat is selected, refresh its messages
    if (this.state.selectedChat) {
      getChatListAdminById(this.state.selectedChat.chatId.toString())
        .then((result) => {
          if (result && result.chatHistory) {
            // Only update if there are new messages
            if (result.chatHistory.length !== this.state.selectedChatHistory.length) {
              this.setState({
                selectedChatHistory: result.chatHistory,
                chatMasterId: result.data._id,
              });
            }
          }
        })
        .catch(() => {});
    }
  };

  scrollToBottom = () => {
    if (this.messagesEndRef.current) {
      this.messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  getAllChats = () => {
    getChatListAdmin().then((result) => {
      this.setState({
        isLoaded: true,
        items: result.chatData || [],
      }, () => {
        // After chats are loaded, check for deep link
        const urlParams = new URLSearchParams(window.location.search);
        const chatId = urlParams.get('chatId');
        if (chatId && !this.state.selectedChat) {
          // Convert both to string for comparison (handles ObjectId vs string mismatch)
          const targetChat = this.state.items.find(chat =>
            String(chat.chatId) === String(chatId)
          );
          if (targetChat) {
            this.selectChat(targetChat);
          } else {
            console.log('Chat not found. Available chats:', this.state.items.map(c => c.chatId));
            console.log('Looking for chatId:', chatId);
          }
        }
      });
    });
  };

  selectChat = (chat) => {
    this.setState({ selectedChat: chat });
    this.loadChatHistory(chat);
  };

  loadChatHistory = (chat) => {
    if (chat.chatId) {
      getChatListAdminById(chat.chatId.toString())
        .then((result) => {
          this.setState({
            selectedChatHistory: result.chatHistory || [],
            chatMasterId: result.data._id,
          });
        })
        .catch((error) => {
          toast.error("Failed to load chat history");
        });
    }
  };

  handleCloseChat = (chatId, status) => {
    if (status === "Close") {
      toast.info("Chat is already closed");
      return;
    }
    closeChat(chatId)
      .then((result) => {
        this.getAllChats();
        toast.success("Chat closed successfully");
        if (this.state.selectedChat?.chatId === chatId) {
          this.setState({
            selectedChat: { ...this.state.selectedChat, chatStatus: "Close" },
          });
        }
      })
      .catch((error) => {
        toast.error("Failed to close chat");
      });
  };

  handleSendMessage = (e) => {
    e.preventDefault();
    if (!this.state.message.trim() || this.state.isSending) return;

    this.setState({ isSending: true });

    const userObject = {
      message: this.state.message,
      chatMasterId: this.state.chatMasterId,
    };

    sendMessageAdmin(userObject)
      .then((response) => {
        if (response.status === 200) {
          this.setState({ message: "" });
          this.loadChatHistory(this.state.selectedChat);
          this.getAllChats();
        } else {
          toast.error(response.message || "Failed to send message");
        }
      })
      .catch((error) => {
        toast.error("Failed to send message");
      })
      .finally(() => {
        this.setState({ isSending: false });
      });
  };

  getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  formatTime = (date) => {
    if (!date) return "";
    return moment.utc(date).tz("Africa/Lubumbashi").format("hh:mm A");
  };

  formatDate = (date) => {
    if (!date) return "";
    const msgDate = moment.utc(date).tz("Africa/Lubumbashi");
    const today = moment().tz("Africa/Lubumbashi").startOf("day");
    const yesterday = moment().tz("Africa/Lubumbashi").subtract(1, "day").startOf("day");

    if (msgDate.isSame(today, "day")) return "Today";
    if (msgDate.isSame(yesterday, "day")) return "Yesterday";
    return msgDate.format("DD/MM/YYYY");
  };

  formatListTime = (date) => {
    if (!date) return "";
    const msgDate = moment.utc(date).tz("Africa/Lubumbashi");
    const today = moment().tz("Africa/Lubumbashi").startOf("day");

    if (msgDate.isSame(today, "day")) {
      return msgDate.format("hh:mm A");
    }
    return msgDate.format("DD/MM/YY");
  };

  getFilteredChats = () => {
    const { items, searchQuery } = this.state;
    if (!searchQuery.trim()) return items;

    return items.filter((chat) =>
      chat.chatFromName?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  renderChatList = () => {
    const filteredChats = this.getFilteredChats();
    const { selectedChat } = this.state;

    if (!filteredChats.length) {
      return (
        <div className="chat-loading">
          <span>No conversations found</span>
        </div>
      );
    }

    return filteredChats.map((chat, index) => (
      <div
        key={chat.chatId || index}
        className={`chat-list-item ${selectedChat?.chatId === chat.chatId ? "active" : ""}`}
        onClick={() => this.selectChat(chat)}
      >
        <div className="chat-avatar">{this.getInitials(chat.chatFromName)}</div>
        <div className="chat-list-info">
          <div className="chat-list-name">{chat.chatFromName || "Unknown"}</div>
          <div className="chat-list-preview">{chat.chatLastMessage || "No messages"}</div>
        </div>
        <div className="chat-list-meta">
          <div className="chat-list-time">{this.formatListTime(chat.updatedAt)}</div>
          <span className={`chat-status-badge ${chat.chatStatus === "Open" ? "open" : "closed"}`}>
            {chat.chatStatus === "Open" ? "Active" : "Closed"}
          </span>
        </div>
      </div>
    ));
  };

  renderMessages = () => {
    const { selectedChatHistory } = this.state;
    let lastDate = null;

    return selectedChatHistory.map((message, index) => {
      const messageDate = this.formatDate(message.createdDate);
      const showDateDivider = messageDate !== lastDate;
      lastDate = messageDate;

      return (
        <React.Fragment key={index}>
          {showDateDivider && (
            <div className="message-date-divider">
              <span>{messageDate}</span>
            </div>
          )}
          <div className={`message-wrapper ${message.fromUser?.role === 1 ? "admin" : "user"}`}>
            <div className="message-bubble">
              <p className="message-text">{message.messageText}</p>
              <div className="message-time">{this.formatTime(message.createdDate)}</div>
            </div>
          </div>
        </React.Fragment>
      );
    });
  };

  render() {
    const { selectedChat, message, isLoaded, isSending } = this.state;

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs title="Chat" breadcrumbItems={this.state.breadcrumbItems} />

            <div className={`chat-app-container ${selectedChat ? "chat-open" : ""}`}>
              {/* Left Sidebar - Chat List */}
              <div className="chat-sidebar">
                <div className="chat-sidebar-header">
                  <h5>Messages</h5>
                </div>
                <div className="chat-search">
                  <div className="chat-search-wrapper">
                    <i className="ri-search-line"></i>
                    <input
                      type="text"
                      placeholder="Search conversations..."
                      value={this.state.searchQuery}
                      onChange={(e) => this.setState({ searchQuery: e.target.value })}
                    />
                  </div>
                </div>
                <div className="chat-list">
                  {!isLoaded ? (
                    <div className="chat-loading">Loading...</div>
                  ) : (
                    this.renderChatList()
                  )}
                </div>
              </div>

              {/* Right Panel - Chat Messages */}
              <div className="chat-main">
                {selectedChat ? (
                  <>
                    <div className="chat-main-header">
                      <div className="chat-avatar">
                        {this.getInitials(selectedChat.chatFromName)}
                      </div>
                      <div className="chat-main-header-info">
                        <div className="chat-main-header-name">
                          {selectedChat.chatFromName || "Unknown"}
                        </div>
                        <div className="chat-main-header-status">
                          {selectedChat.chatStatus === "Open" ? "Active chat" : "Chat closed"}
                        </div>
                      </div>
                      <div className="chat-main-actions">
                        {selectedChat.chatStatus === "Open" && (
                          <button
                            className="btn-close-chat"
                            onClick={() =>
                              this.handleCloseChat(
                                selectedChat.chatId.toString(),
                                selectedChat.chatStatus
                              )
                            }
                          >
                            Close Chat
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="chat-messages">
                      {this.renderMessages()}
                      <div ref={this.messagesEndRef} />
                    </div>

                    {selectedChat.chatStatus === "Open" && (
                      <form className="chat-input-area" onSubmit={this.handleSendMessage}>
                        <div className="chat-input-wrapper">
                          <input
                            type="text"
                            placeholder="Type a message..."
                            value={message}
                            onChange={(e) => this.setState({ message: e.target.value })}
                          />
                        </div>
                        <button
                          type="submit"
                          className="chat-send-btn"
                          disabled={!message.trim() || isSending}
                        >
                          <i className="ri-send-plane-2-fill"></i>
                        </button>
                      </form>
                    )}
                  </>
                ) : (
                  <div className="chat-empty-state">
                    <i className="ri-chat-3-line"></i>
                    <h4>Select a conversation</h4>
                    <p>Choose a chat from the list to start messaging</p>
                  </div>
                )}
              </div>
            </div>
          </Container>
        </div>
        <ToastContainer />
      </React.Fragment>
    );
  }
}

export default ChatList;
